import { useCallback, useMemo } from 'react';

import { useParams } from '../../../contexts/RouterContext';
import { nuc, openInNewTab } from '../../../utils';
import type { RevisionRouteParams } from '../../projects/projectDashboardTypes';
import type { ReportV3Format } from '../types';

import { useReportOptionsParams } from './useReportOptionsParams';

/**
 * Hook that returns a callback to download a project report by opening the
 * download endpoint in a new tab. The browser handles the file download via
 * Content-Disposition headers set by the server.
 *
 * Release group reports are generated in the background instead; see `useQueueReportDownload`.
 *
 * @param format The report format.
 */
export function useDownloadReport(format: ReportV3Format) {
  const params = useParams<RevisionRouteParams>();
  const reportOptionsParams = useReportOptionsParams();

  const url = useMemo(() => {
    const projectLocator = nuc(`${params.project_id}$${params.revision_id}`);
    return `/api/v2/revisions/${projectLocator}/attribution/download?download=true&format=${format}${reportOptionsParams ? `&${reportOptionsParams}` : ''}`;
  }, [format, params, reportOptionsParams]);

  const downloadReport = useCallback(() => {
    openInNewTab(url);
  }, [url]);

  return { downloadReport };
}
